if (!customElements.get("product-tabs")) {
	class ProductTabs extends HTMLElement {
		constructor() {
			super();

			// Configuration from data attributes
			this.config = {
				sectionId: this.dataset.sectionId || "",
				headingSuffix:
					this.dataset.headingSuffix || "- Best Fit and Comfort Seat Covers",
				fallbackHeading:
					this.dataset.fallbackHeading || "Seat Covers - Best Fit and Comfort",
				noContentHeading:
					this.dataset.noContentHeading || "No Content Available",
				noContentMessage:
					this.dataset.noContentMessage ||
					"Please add a content section to the page with the product information.",
				faqHeading: this.dataset.faqHeading || "Frequently Asked Questions",
			};

			// Cache DOM selectors for performance
			this.elements = {
				tabButtons: null,
				tabPanels: null,
				mainTitle: null,
				tabs: {},
			};
		}

		connectedCallback() {
			this.cacheElements();
			this.loadExternalContent();
			this.bindEvents();
		}

		cacheElements() {
			this.elements.tabButtons = this.querySelectorAll('[role="tab"]');
			this.elements.tabPanels = this.querySelectorAll('[role="tabpanel"]');
			this.elements.mainTitle = this.querySelector(".product-tabs__title");

			// Cache individual tab panels
			this.elements.tabs = {
				overview: this.querySelector('[data-tab-content="overview"]'),
				faq: this.querySelector('[data-tab-content="faq"]'),
				interior: this.querySelector('[data-tab-content="interior"]'),
			};
		}

		bindEvents() {
			this.elements.tabButtons.forEach((button) => {
				button.addEventListener("click", (e) => this.handleTabClick(e));
				button.addEventListener("keydown", (e) => this.handleTabKeydown(e));
			});
		}

		handleTabClick(event) {
			const button = event.currentTarget;
			const targetTab = button.dataset.tab;
			this.switchTab(targetTab);
		}

		handleTabKeydown(event) {
			const currentButton = event.currentTarget;
			const buttons = Array.from(this.elements.tabButtons);
			const currentIndex = buttons.indexOf(currentButton);

			let nextIndex = currentIndex;

			switch (event.key) {
				case "ArrowLeft":
					event.preventDefault();
					nextIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
					break;
				case "ArrowRight":
					event.preventDefault();
					nextIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
					break;
				case "Home":
					event.preventDefault();
					nextIndex = 0;
					break;
				case "End":
					event.preventDefault();
					nextIndex = buttons.length - 1;
					break;
				default:
					return;
			}

			buttons[nextIndex].focus();
			const targetTab = buttons[nextIndex].dataset.tab;
			this.switchTab(targetTab);
		}

		switchTab(tabName) {
			this.elements.tabButtons.forEach((btn) => {
				const isActive = btn.dataset.tab === tabName;
				btn.classList.toggle("product-tab-button--active", isActive);
				btn.setAttribute("aria-selected", isActive);
				btn.setAttribute("tabindex", isActive ? "0" : "-1");
			});

			this.elements.tabPanels.forEach((panel) => {
				const isActive = panel.dataset.tabContent === tabName;
				panel.classList.toggle("product-tab--active", isActive);
				panel.hidden = !isActive;
			});
		}

		/**
		 * Find the content section on the page
		 */
		findContentSection() {
			const currentSection = this.closest('[id^="shopify-section-"]');

			if (!currentSection) {
				console.warn("[ProductTabs] Could not find current section");
				return null;
			}

			const searchPatterns = [
				".page-width--narrow .rte",
				".page-width .rte",
				".rte",
			];

			const allSections = document.querySelectorAll('[id^="shopify-section-"]');

			for (const section of allSections) {
				if (section === currentSection) continue;

				for (const pattern of searchPatterns) {
					const rteContent = section.querySelector(pattern);
					if (!rteContent) continue;

					const headers = Array.from(
						rteContent.querySelectorAll("h2.section-header--title, h2")
					);
					const hasReasons = headers.some((h) =>
						h.textContent.toLowerCase().includes("reasons to get")
					);
					const hasFaq = headers.some((h) =>
						h.textContent.toLowerCase().includes("frequently asked")
					);
					const hasInterior = headers.some((h) =>
						h.textContent.toLowerCase().includes("interior trim")
					);

					if (hasReasons || hasFaq || hasInterior) {
						return { section, rteContent };
					}
				}
			}

			return null;
		}

		loadExternalContent() {
			const found = this.findContentSection();

			if (!found) {
				console.warn("[ProductTabs] Content section not found on the page");
				this.showNoContentMessage();
				return;
			}

			const { section: externalSection, rteContent } = found;
			const headers = Array.from(
				rteContent.querySelectorAll("h2.section-header--title, h2")
			);

			// Process Overview/Reasons section
			const reasonsHeader = headers.find((h) =>
				h.textContent.toLowerCase().includes("reasons to get")
			);
			if (reasonsHeader) {
				const overviewContent = this.extractContentUntilNextH2(reasonsHeader);
				this.populateOverviewTab(overviewContent, reasonsHeader.textContent);
				this.updateMainTitle(reasonsHeader.textContent);
			}

			// Process FAQ section
			const faqHeader = headers.find((h) =>
				h.textContent.toLowerCase().includes("frequently asked")
			);
			if (faqHeader) {
				const faqContent = this.extractContentUntilNextH2(faqHeader);
				this.populateFAQTab(faqContent);
			}

			const interiorHeader = headers.find((h) =>
				h.textContent.toLowerCase().includes("interior trim")
			);
			if (interiorHeader) {
				const interiorContent = this.extractContentUntilNextH2(interiorHeader);
				this.populateInteriorTab(interiorContent, interiorHeader.textContent);
			}

			if (externalSection) {
				externalSection.style.display = "none";
			}
		}

		getCleanText(htmlString) {
			const tempDiv = document.createElement("div");
			tempDiv.innerHTML = htmlString;
			return tempDiv.textContent || tempDiv.innerText || htmlString;
		}

		updateMainTitle(headerText) {
			if (!this.elements.mainTitle) return;

			const cleanText = this.getCleanText(headerText);
			this.elements.mainTitle.textContent =
				cleanText.replace(/^reasons to get a\s*/i, "") +
				" " +
				this.config.headingSuffix;
		}

		showNoContentMessage() {
			const overviewTab = this.elements.tabs.overview;
			if (!overviewTab) return;

			const heading = overviewTab.querySelector(".product-tab__heading");
			const content = overviewTab.querySelector(".product-tab__content");

			if (heading) heading.textContent = this.config.noContentHeading;
			if (content) content.innerHTML = `<p>${this.config.noContentMessage}</p>`;
		}

		extractContentUntilNextH2(startElement) {
			const content = [];
			let currentElement = startElement.nextElementSibling;

			while (currentElement && currentElement.tagName !== "H2") {
				content.push(currentElement.cloneNode(true));
				currentElement = currentElement.nextElementSibling;
			}

			return content;
		}

		createListItem(number, title, text, shouldStripNumber = false) {
			const listItem = document.createElement("li");
			listItem.className = "product-tab__list-item";

			const numberSpan = document.createElement("span");
			numberSpan.className = "product-tab__list-item-number";
			numberSpan.textContent = number + ".";

			const titleSpan = document.createElement("span");
			titleSpan.className = "product-tab__list-item-title";
			titleSpan.textContent = shouldStripNumber
				? title.replace(/^\d+\.\s*/, "")
				: title;

			const textDiv = document.createElement("div");
			textDiv.className = "product-tab__list-item-text";
			textDiv.innerHTML = text;

			listItem.appendChild(numberSpan);
			listItem.appendChild(titleSpan);
			listItem.appendChild(textDiv);

			return listItem;
		}

		populateListTab(tab, contentElements, headingText = null) {
			if (!tab) return;

			const heading = tab.querySelector(".product-tab__heading");
			const contentContainer = tab.querySelector(".product-tab__content");
			if (!contentContainer) return;

			// Update heading if provided
			if (heading && headingText) {
				heading.textContent = this.getCleanText(headingText);
			}

			// Create list
			const list = document.createElement("ol");
			list.className = "product-tab__list";

			let counter = 1;
			for (let i = 0; i < contentElements.length; i += 2) {
				const h4 = contentElements[i];
				const p = contentElements[i + 1];

				if (h4 && h4.tagName === "H4" && p && p.tagName === "P") {
					const listItem = this.createListItem(
						counter,
						h4.textContent,
						p.innerHTML,
						true // Strip numbers from overview items
					);
					list.appendChild(listItem);
					counter++;
				}
			}

			contentContainer.innerHTML = "";
			contentContainer.appendChild(list);
		}

		populateOverviewTab(contentElements, headingText) {
			this.populateListTab(
				this.elements.tabs.overview,
				contentElements,
				headingText
			);
		}

		populateFAQTab(contentElements) {
			this.populateListTab(
				this.elements.tabs.faq,
				contentElements,
				this.config.faqHeading
			);
		}

		populateInteriorTab(contentElements, headingText) {
			const interiorTab = this.elements.tabs.interior;
			if (!interiorTab) return;

			const heading = interiorTab.querySelector(".product-tab__heading");
			const contentContainer = interiorTab.querySelector(
				".product-tab__content"
			);
			if (!contentContainer) return;

			// Update heading
			if (heading && headingText) {
				heading.textContent = this.getCleanText(headingText);
			}

			contentContainer.innerHTML = "";
			contentElements.forEach((element) => {
				contentContainer.appendChild(element);
			});
		}
	}

	customElements.define("product-tabs", ProductTabs);
}
